import { useMemo } from 'react';
import Dagre from '@dagrejs/dagre';
import { Node, Edge, useStore } from '@xyflow/react';
import { DefaultNodeData } from './data';

const DEFAULT_NODE_WIDTH = 320;
const DEFAULT_NODE_HEIGHT = 74;
const INLINE_NODE_GAP = 40;

export const calculateLayout = async (
  nodes: Node<DefaultNodeData>[],
  edges: Edge[],
  options: { direction: string; ranksep?: number }
): Promise<{ nodes: Node<DefaultNodeData>[]; edges: Edge[]; graphWidth: number; graphHeight: number }> => {
  // Create a new Dagre graph instance
  const dagreGraph = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

  // Configure the graph with the specified direction and separations
  dagreGraph.setGraph({ rankdir: options.direction ?? 'TB', ranksep: options.ranksep, nodesep: 70 });

  // Add edges and nodes to the graph using the nodes/edges from the props
  edges.forEach((edge) => {
    if (edge.data?.useManualLayout) {
      return;
    }
    dagreGraph.setEdge(edge.source, edge.target);
  });
  nodes.forEach((node) =>
    dagreGraph.setNode(node.id, {
      ...node,
      width: node.measured?.width ?? DEFAULT_NODE_WIDTH,
      height: node.measured?.height ?? DEFAULT_NODE_HEIGHT,
    })
  );

  // Calculate the layout to get x and y positions on the nodes
  // NOTE: Need to disable optimal order heuristic to prevent nodes from being reordered
  Dagre.layout(dagreGraph, { disableOptimalOrderHeuristic: true });

  // Post-process Y positions to remove extra spacing between ranks that Dagre adds to accommodate varying node heights.
  // Dagre assigns each node a Y equal to the top edge of its rank (layer) plus half the
  // tallest node in that rank: `y = prevY + maxHeight / 2`. This centers each node
  // vertically within the space reserved for its rank.
  //
  // We want nodes flush with the top of their rank instead (i.e. `y = prevY`), which
  // prevents a visible "jump" when nodes are added/removed and maxHeight changes.
  //
  // To undo the centering offset without modifying dagre internals, we:
  //   1. Group nodes by their post-layout Y value — all nodes sharing the same Y
  //      belong to the same rank (dagre guarantees this for TB/BT graphs).
  //   2. For each rank, find maxHeight and subtract `maxHeight / 2` from every node's Y,
  //      reversing the centering dagre added.
  const nodeIds = dagreGraph.nodes();

  // Pass 1: bucket nodes by rank (same Y value = same rank after dagre layout).
  const nodesByY = new Map<number, string[]>();
  for (const id of nodeIds) {
    const n = dagreGraph.node(id);
    if (!n) continue;
    if (!nodesByY.has(n.y)) {
      nodesByY.set(n.y, []);
    }
    const rank = nodesByY.get(n.y);
    if (rank) rank.push(id);
  }

  // Pass 2: shift every node in each rank up by maxHeight/2, removing the centering offset.
  for (const [y, ids] of nodesByY) {
    const maxHeight = Math.max(...ids.map((id) => dagreGraph.node(id).height));
    for (const id of ids) {
      dagreGraph.node(id).y = y - maxHeight / 2;
    }
  }

  // There was a strange bug that would occur when expanding the first child node (after the root start node)
  // that would cause the y position of the root start (index 0) and the first child (index 1) to change causing
  // a "jump" effect where the root start node would move down from its starting y position.
  // In order to prevent this, we set the y position of the start node to 0 and subtract the yOffset from all other nodes.
  const getCustomYPosition = (node: Node<DefaultNodeData>, yOffset: number, dagrePosition?: { y: number }) => {
    // NOTE: This is assuming the first node is the root start node so this will not work if the first node is not the root start node.
    const startNode = nodes[0];
    if (node.id === startNode.id) {
      return 0; // Fixed position for the root start node
    }

    if (!dagrePosition) {
      return node.position?.y ?? 0;
    }

    // For all other nodes we calculate the y position based on the graph layout and subtract the yOffset
    // to ensure they are positioned correctly relative to the root start node.
    return dagrePosition.y - yOffset;
  };

  const startNode = nodes[0];
  const startNodePosition = startNode ? dagreGraph.node(startNode.id) : undefined;
  const startNodeYOffset = Math.abs(0 - (startNodePosition?.y ?? 0));

  // Get the overall width and height of the graph
  const graphWidth = dagreGraph.graph().width || 0;
  const graphHeight = dagreGraph.graph().height || 0;

  // First pass: apply Dagre positions to every node (standard flow layout).
  const positionedNodes = nodes.map((node) => {
    const dagrePosition = dagreGraph.node(node.id);

    return {
      ...node,
      position: {
        x:
          dagrePosition?.x !== undefined
            ? dagrePosition.x - (node.measured?.width ?? DEFAULT_NODE_WIDTH) / 2
            : node.position?.x ?? 0,
        y: getCustomYPosition(node, startNodeYOffset, dagrePosition), // Use the custom function to determine the y position
      },
      type: node.type || 'basic', // Ensure type is always defined
      hidden: false, // Make sure nodes are visible after layout
    };
  });

  // Second pass: for edges marked useManualLayout, manually place the target node
  // inline next to the source node instead of using Dagre's parent/child placement.
  const nodeById = new Map(positionedNodes.map((node) => [node.id, node]));
  edges.forEach((edge) => {
    if (!edge.data?.useManualLayout) {
      return;
    }

    const sourceNode = nodeById.get(edge.source);
    const targetNode = nodeById.get(edge.target);
    if (!sourceNode || !targetNode) {
      return;
    }

    // sourceNode.position is top-left; add measured width so the target starts
    // from the source node's right edge, then apply a fixed horizontal gap.
    const sourceWidth = sourceNode.measured?.width ?? DEFAULT_NODE_WIDTH;
    targetNode.position = {
      x: sourceNode.position.x + sourceWidth + INLINE_NODE_GAP,
      y: sourceNode.position.y,
    };
  });

  return {
    // Map the nodes from the props to include their new x and y positions which we get
    // from the calculated nodes in the Dagre graph
    nodes: positionedNodes.map((node) => {
      const dagrePosition = dagreGraph.node(node.id);

      return {
        ...node,
        position: {
          x:
            node.position.x ??
            (dagrePosition?.x !== undefined ? dagrePosition.x - (node.measured?.width ?? DEFAULT_NODE_WIDTH) / 2 : 0),
          y: node.position.y ?? getCustomYPosition(node, startNodeYOffset, dagrePosition), // Use the custom function to determine the y position
        },
        type: node.type || 'basic', // Ensure type is always defined
        hidden: false, // Make sure nodes are visible after layout
      };
    }),
    edges: edges.map((edge) => {
      return { ...edge, hidden: false };
    }),
    graphWidth,
    graphHeight,
  };
};

type Elements = {
  nodes: Array<Node>;
  edges: Array<Edge>;
};

export function compareElements(xs: Elements, ys: Elements) {
  return nodesEqualityFn(xs.nodes, ys.nodes) && edgesEqualityFn(xs.edges, ys.edges);
}

/**
 * Custom equality function to compare two node arrays by dimension only.
 *
 * Ignores position changes and short-circuits to `true` (equal) while any node
 * is actively being resized or dragged, preventing unwanted layout recalculations
 * during user interaction.
 *
 * @param xs - The previous array of nodes.
 * @param ys - The next array of nodes.
 * @returns `true` if the arrays are considered equal (same length, same measured
 *   dimensions for every node, and no node is currently being resized/dragged);
 *   `false` if a layout recalculation should be triggered.
 */
export function nodesEqualityFn(xs: Array<Node>, ys: Array<Node>) {
  // the number of nodes changed, so we already know that the nodes are not equal
  if (xs.length !== ys.length) {
    return false;
  }

  for (let i = 0; i < xs.length; i++) {
    const x = xs[i];
    const y = ys[i];

    // the node doesn't exist in the next state so it just got added
    if (!y) {
      return false;
    }
    // We don't want to force a layout change while a user might be resizing a
    // node, so we only compare the dimensions if the node is not currently
    // being resized.
    //
    // We early return here instead of using a `continue` because there's no
    // scenario where we'd want nodes to start moving around *while* a user is
    // trying to resize a node or move it around.
    if (x.resizing || x.dragging) {
      return true;
    }

    // the dimensions have changed
    if (x.measured?.width !== y.measured?.width || x.measured?.height !== y.measured?.height) {
      return false;
    }
  }

  return true;
}

/**
 * Custom equality function to compare two edge arrays by connectivity only.
 *
 * Compares each edge's `source`, `target`, `sourceHandle`, and `targetHandle`.
 * Data, styles, and other metadata are intentionally ignored so that cosmetic
 * updates do not trigger a layout recalculation.
 *
 * @param xs - The previous array of edges.
 * @param ys - The next array of edges.
 * @returns `true` if the arrays are considered equal (same length and identical
 *   source/target/handle pairs for every edge); `false` if a layout
 *   recalculation should be triggered.
 */
export function edgesEqualityFn(xs: Array<Edge>, ys: Array<Edge>) {
  // the number of edges changed, so we already know that the edges are not equal
  if (xs.length !== ys.length) {
    return false;
  }

  for (let i = 0; i < xs.length; i++) {
    const x = xs[i];
    const y = ys[i];

    // the edge's source or target changed
    if (x.source !== y.source || x.target !== y.target) {
      return false;
    }
    if (x?.sourceHandle !== y?.sourceHandle) {
      return false;
    }
    if (x?.targetHandle !== y?.targetHandle) {
      return false;
    }
  }

  return true;
}

// Custom hook to get canvas dimensions from the React Flow store
export const useCanvasDimensions = () => {
  const { width, height } = useStore((state) => ({
    width: state.width,
    height: state.height,
  }));

  const dimensions = useMemo(() => ({ width, height }), [width, height]);

  return dimensions;
};
